/*
  This code is the intellectual property of Bulb Inc. and other software publishers;
  it may not be altered, copied or disclosed without prior written approval.
  Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { Inject, Injectable } from '@angular/core';
import { PluginTarget } from '@uppy/core';
import type UppyT from '@uppy/core';

import { first } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { BulbCKEStateService } from 'src/ckeditor/editor/services/bulb-cke-state.service';
import { availableLangs } from '../../exports';
import type { BulbCloseUppyAndCallOptions } from './plugins/bulb-close-uppy-and-call';
import { AssetUploadEventService } from '../asset-upload-event.service';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';
import { BULB_BUSY_COVER_SERVICE } from '../../angularjs.providers';
import { LANGUAGE_MAP } from '../uppy-config';
import { TranslationService } from '../../../common/translation.service';
import { AppConfigService } from '../../../common/services/app-config/app-config.service';
import { CanvaUppyService } from '../canva-uppy.service';
import { CanvaFormatDialogComponent } from '../canva-format-dialog/canva-format-dialog.component';

const loader = availableLangs.reduce((acc, lang) => {
  acc[lang] = () => import(`~/resources/translations/locale-${lang}.json`);
  return acc;
}, {});

declare const CCEverywhere: any;

/**
 * Provides methods for loading custom Bulb Uppy plugins.
 */
@Injectable({
  providedIn: 'root',
})
export class UppyCustomDashboardPluginsService {
  private scope = {
    scope: 'ajs',
    loader,
  };

  /**
   * Adobe Express module instance returned from SDK initialization
   */
  module: any;

  /**
   * Adobe Express editor instance used to create/edit documents
   */
  editor: any;

  /**
   * Indicates whether Adobe Express SDK is initialized and ready
   */
  private ccReady = false;

  /**
   * Function to hide busy loader (returned from busy service)
   */
  hideBusy;

  constructor(
    private aeroDialogService: AeroDialogService,
    private appConfigService: AppConfigService,
    private canvaUppyService: CanvaUppyService,
    private eventBus: AssetUploadEventService,
    private transloco: TranslocoService,
    private translationService: TranslationService,
    private ckeStateService: BulbCKEStateService,
    @Inject('BulbAssetLibraryUnsplashService') private BulbAssetLibraryUnsplashService,
    @Inject('BulbAssetLibraryService') private BulbAssetLibraryService,
    @Inject('BulbErrorService') private bulbErrorService: any,
    @Inject('BulbAuthService') private bulbAuthService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
  ) {}

  /**
   * Adds Adobe Express button to Uppy Dashboard.
   *
   * - Loads translated label
   * - Registers custom plugin
   * - Initializes Adobe Express SDK (once)
   * - Opens editor on click
   * - Passes user language as locale
   *
   * @param uppyInstance Uppy instance
   * @param target Dashboard target container
   */
  public async useAdobeExpressPlugin(
    uppyInstance: UppyT,
    target: PluginTarget,
  ): Promise<void> {
    const translationPromise = this.transloco
      .selectTranslate('adobe_express', {}, this.scope)
      .pipe(first())
      .toPromise();

    const [
      BulbAdobeExpress, title,
    ] = await Promise.all([
      import('./plugins/bulb-close-uppy-and-call'),
      translationPromise,
    ]);

    const BulbAdobeExpressOptions: BulbCloseUppyAndCallOptions = {
      id: 'bulb-adobe-express',
      title,
      iconSrc: 'resources/images/adobe_express.png',
      target,
      onClick: async () => {
        const initializeParams = {
          clientId: this.appConfigService.adobe.clientId,
          appName: this.appConfigService.adobe.appName,
        };

        const authOption = {
          mode: 'upfront',
        };

        const configParams = {
          locale: LANGUAGE_MAP[this.bulbAuthService.currentUser.language.toLowerCase()],
        };

        if (!this.editor) {
          const result = await (window as any).CCEverywhere.initialize(
            initializeParams,
            configParams,
            authOption,
          );

          this.module = result.module;
          this.editor = result.editor;
          this.ccReady = true;
        }

        this.createImageWithAdobe(uppyInstance);
      },
    };
    uppyInstance.use(BulbAdobeExpress.default, { ...BulbAdobeExpressOptions });
  }

  /**
   * Adds a Canva action to Uppy. Canva runs in a popup, then the exported PNG files are added to the existing Uppy
   * instance.
   * OAuth, tokens, return-JWT validation, and Canva download URLs remain on the BULB server.
   *
   * @param uppyInstance Uppy instance that receives the finished Canva images
   * @param target Uppy Dashboard target
   * @param uploadState
   */
  public async useCanvaPlugin(
    uppyInstance: UppyT,
    target: PluginTarget,
    assetType: string,
    uploadState?: string,
    orientation?: string,
  ): Promise<void> {
    const translationPromise = this.transloco
      .selectTranslate('canva', {}, this.scope)
      .pipe(first())
      .toPromise();

    const [BulbCanva, title] = await Promise.all([
      import('./plugins/bulb-close-uppy-and-call'),
      translationPromise,
    ]);

    const options: BulbCloseUppyAndCallOptions = {
      id: 'bulb-canva',
      title,
      iconSrc: 'resources/images/canva.png',
      target,
      onClick: () => {
        if (assetType === 'attached_file') {
          const ref = this.aeroDialogService.show(CanvaFormatDialogComponent, {
            showHeader: false,
            styleClass: 'aero-dialog',
            width: '350px',
          });

          ref.onClose.subscribe((selectedFormat: 'png' | 'pptx' | null) => {
            if (selectedFormat) {
              this.canvaUppyService.openEditor(uppyInstance, assetType, uploadState, orientation, selectedFormat);
            }
          });
        } else {
          this.canvaUppyService.openEditor(uppyInstance, assetType, uploadState, orientation, 'png');
        }
      },
    };
    uppyInstance.use(BulbCanva.default, { ...options });
  }

  public async useAssetLibraryPlugin(
    uppyInstance: UppyT,
    target: PluginTarget,
    uploadState: any,
    contentBlockData?: any,
  ): Promise<void> {
    const translationPromise = this.transloco
      .selectTranslate('asset_library', {}, this.scope)
      .pipe(first())
      .toPromise();

    const [
      BulbAssetLibrary, { bulbAssetLibraryIcon }, title,
    ] = await Promise.all([
      import('./plugins/bulb-close-uppy-and-call'),
      import('IconLibrary/bulb/uppy-plugins/asset-library'),
      translationPromise,
    ]);

    const BulbAssetLibraryOptions: BulbCloseUppyAndCallOptions = {
      id: 'bulb-asset-library',
      title,
      iconSvg: bulbAssetLibraryIcon.data.replace('<svg', '<svg width="25" height="25"'),
      target,
      onClick: () => {
        const assetType = this.ckeStateService.assetType.value;
        this.BulbAssetLibraryService.dialog.onlyAssetLibrary.showSelectionModal((entry) => {
          const mappedResponse = {
            entry,
            // Note: I have no idea what these other properties are for (yet).
            used: false,
            usage: {
              collections: [],
            },
          };
          this.eventBus.handleOnProcessingSuccess(mappedResponse, uploadState, contentBlockData);
          // Returning a promise to allow showBusyDuring to work since it expects a returned promise to hide
          return new Promise<void>((resolve) => {
            resolve();
          });
        }, assetType);
      },
    };
    uppyInstance.use(BulbAssetLibrary.default, { ...BulbAssetLibraryOptions });
  }

  public async useUnsplashPlugin(
    uppyInstance: UppyT,
    target: PluginTarget,
    folderId: string,
    uploadState: any,
    contentBlockData?: any,
  ): Promise<void> {
    const translationPromise = this.transloco
      .selectTranslate('assetlibrary_selection_modal_tab_web_search', {}, this.scope)
      .pipe(first())
      .toPromise();

    const [
      BulbUnsplash, { bulbWebSearchIcon }, title,
    ] = await Promise.all([
      import('./plugins/bulb-close-uppy-and-call'),
      import('IconLibrary/bulb/uppy-plugins/web-search'),
      translationPromise,
    ]);
    const bulbUnsplashOptions: BulbCloseUppyAndCallOptions = {
      id: 'bulb-unsplash',
      title,
      target,
      iconSvg: bulbWebSearchIcon.data.replace('<svg', '<svg width="25" height="25"'),
      onClick: () => {
        this.BulbAssetLibraryUnsplashService.openUnsplashImagePicker(folderId, (resp: { entry: any; }) => {
          this.eventBus.handleOnProcessingSuccess(resp, uploadState, contentBlockData);
          // For some reason when this modal gets closed it doesn't remove the `overflow: none;` style added to the body
          // by the modal library we are using.
          // In other cases we are using the `this.BulbAssetLibraryService.dialog.closeCurrentModalKeyPress(event);`
          // method but for some reason that doesn't work here.
          // It also isn't possible to remove this with a class or such since the modal library adds the style directly
          // to the `element.style` attribute so this is the only way to change the style for it.
          // This should probably be fixed in a different way in the future.
          document.body.style.removeProperty('overflow');
        });
      },
    };

    uppyInstance.use(BulbUnsplash.default, { ...bulbUnsplashOptions });
  }

  public async usePremiumPlugin(
    uppyInstance: UppyT,
    target: PluginTarget,
    name: string,
  ): Promise<void> {
    const translationPromise = this.transloco
      .selectTranslate(name, {}, this.scope)
      .pipe(first())
      .toPromise();

    const [
      BulbPremium, { bulbBulbPremiumUppyIconIcon }, title,
    ] = await Promise.all([
      import('./plugins/bulb-close-uppy-and-call'),
      import('IconLibrary/bulb/uppy-plugins/bulb-premium-uppy-icon'),
      translationPromise,
    ]);

    const BulbPremiumOptions: BulbCloseUppyAndCallOptions = {
      id: `bulb-embed-${name}`,
      title,
      iconSvg: bulbBulbPremiumUppyIconIcon.data.replace('<svg', '<svg width="25" height="25"'),
      target,
      onClick: () => {
        this.aeroDialogService.showPlanUpgradeDialog();
      },
    };
    uppyInstance.use(BulbPremium.default, { ...BulbPremiumOptions });
  }

  public async useEmbedPlugin(
    uppyInstance: UppyT,
    target: PluginTarget,
  ): Promise<void> {
    const translationPromise = this.transloco
      .selectTranslate('editor_media_bar_embed_url', {}, this.scope)
      .pipe(first())
      .toPromise();

    const [
      BulbEmbed, { bulbWebSearchIcon }, title,
    ] = await Promise.all([
      import('./plugins/bulb-close-uppy-and-call'),
      import('IconLibrary/bulb/uppy-plugins/web-search'),
      translationPromise,
    ]);

    const BulbEmbedOptions: BulbCloseUppyAndCallOptions = {
      id: 'bulb-embed',
      title,
      iconSvg: bulbWebSearchIcon.data.replace('<svg', '<svg width="25" height="25"'),
      target,
      onClick: () => {
        this.eventBus.openEmbedModal.next(null);
      },
    };
    uppyInstance.use(BulbEmbed.default, { ...BulbEmbedOptions });
  }

  /**
   * Opens Adobe Express editor and handles publish/upload flow.
   *
   * - Validates Adobe SDK readiness
   * - Configures callbacks (publish, cancel, error)
   * - Converts exported asset to File
   * - Adds file to Uppy and triggers upload
   * - Shows/hides busy loader during upload
   * - Initializes editor with app, export, and container configs
   *
   * @param uppy Uppy instance used to upload generated asset
   */
  createImageWithAdobe(uppy) {
    if (!this.ccReady || !this.module) {
      console.error('Adobe CC Everywhere module is not ready.');
      return;
    }

    // Callbacks to be used when creating or editing a document
    const callbacks = {
      onCancel: () => {},
      onPublish: async (intent, publishParams: any) => {
        const asset = publishParams.asset[0];

        let file;

        // 🔥 get mime type safely
        const mimeType = asset.fileType
          || (asset.data?.match(/^data:(.*?);/)?.[1])
          || 'image/png';

        const extension = this.getExtensionFromMime(mimeType);

        const fileName = `adobe_image_${Date.now()}.${extension}`;

        if (asset.data instanceof Blob) {
          file = new File([asset.data], 'edited-image', {
            type: asset.data.type,
          });
        } else if (asset.dataType === 'base64') {
          file = this.base64ToFile(asset.data, fileName);
        } else {
          console.error('Unsupported asset data type:', asset.dataType);
        }

        uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          projectId: publishParams.projectId,
          source: 'AdobeExpress',
        });

        uppy.upload();
      },
      onError: (err) => {
        this.bulbErrorService.showError(null, err);
      },
    };

    uppy.on('upload', () => {
      this.hideBusy = this.bulbBusyCoverService.showBusy();
    });

    uppy.on('complete', () => {
      this.hideBusy();
    });

    // Configuration for the app, shared by both Create and Edit flows
    const appConfig = {
      selectedCategory: 'media',
      allowedFileTypes: ['image/png', 'image/jpeg'],
      multiPage: true,
      callbacks,
    };

    // Configuration for the export options made available to the user
    // when creating or editing a document
    const exportConfig = [
      {
        id: 'save-modified-asset',
        label: this.translationService.translateInstant('save_in_bulb'),
        action: { target: 'publish', closeTargetOnExport: true },
        style: { uiType: 'button' },
      },
    ];

    // Container configuration
    const containerConfig = {
      width: '100%',
      height: '600px',
      showDarkerBackgroundForLoader: true,
      showExpressIconWithText: true,
    };

    // Presetting the canvas size
    const canvasSize = 'BusinessCard';
    const docConfig = {
      canvasSize,
    };
    // Using the global appConfig and exportConfig
    try {
      this.startAdobeContainerWatcher();
      this.editor.create(docConfig, appConfig, exportConfig, containerConfig);
    } catch (error) {
      console.error('Failed to launch editor:', error);
    }
  }

  /**
   * Returns file extension based on MIME type.
   *
   * - Validates MIME is present and is an image
   * - Maps known image types (png, jpeg, jpg)
   * - Falls back to extracting extension from MIME string
   *
   * @param mime Image MIME type (e.g. image/png)
   * @returns File extension (e.g. png, jpg)
   * @throws Error if MIME is missing or not an image
   */
  getExtensionFromMime(mime: string): string {
    if (!mime) {
      throw new Error('Invalid file: MIME type is missing');
    }

    // 🔥 ensure it's an image
    if (!mime.startsWith('image/')) {
      throw new Error(`Invalid file type: ${mime}. Only images are allowed.`);
    }

    const map: any = {
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
    };

    return map[mime] || mime.split('/')[1];
  }

  /**
   * Converts a base64 string to a File object.
   *
   * - Extracts MIME type from base64 header
   * - Decodes base64 into binary
   * - Creates a File for upload usage
   *
   * @param base64 Base64 encoded string (data URL format)
   * @param fileName Desired file name
   * @returns File object
   */
  base64ToFile(base64: string, fileName: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
  }

  /**
   * Observes DOM and adjusts Adobe Express container styles.
   *
   * - Detects dynamically injected Adobe container element
   * - Forces it to fullscreen with high z-index
   * - Stops observing once container is found
   *
   * Used to ensure Adobe editor overlays correctly above app UI.
   */
  startAdobeContainerWatcher() {
    const observer = new MutationObserver(() => {
      const el = Array.from(document.querySelectorAll('*'))
        .find((e) => e.tagName.toLowerCase().startsWith('cc-everywhere-container'));

      if (el) {
        Object.assign((el as HTMLElement).style, {
          zIndex: '2147483647',
          position: 'fixed',
          inset: '0',
        });

        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

}
